import {
  Observable,
  catchError,
  concatMap,
  forkJoin,
  map,
  of,
  tap,
} from 'rxjs';

import { AppConfigService } from '../../services/app-config.service/app-config.service';
import { AuthenticationResult } from '@azure/msal-browser';
import { ChatWidgetService } from '../../services/chat-widget/chat-widget.service';
import { FetchCMSContentService } from '../../services/fetch-cms-content/fetch-cms-content.service';
import { InactivityService } from '../../services/inactivity-service/inactivity.service';
import { LocaleService } from '../../services/locale-wrapper/locale.service';
import { LoginService } from '../../services/login-service/login.service';
import { MonitoringService } from '../../services/app-monitoring-service/app-monitoring.service';
import { MsalService } from '@azure/msal-angular';
import { ThemeService } from '../../services/theme-service/theme.service';
import { endPoints } from '../../constants/endpoints';
import { Auth0TokenService } from '../../services/auth0-service/auth0-service';
import { ApiService } from '../../services/api-service/api-service';
import { IKeyValuePair } from '../../models/interfaces/IObject';
import { SessionStorageUtility } from '../../utils/sessionStorageUtils';
import { SESSION_STORAGE_KEYS } from '../../constants/storage.constant';
import { LoginSource } from '../../constants/global.constant';

/**
 *
 * @param loginService
 * @param appConfigService
 * @param monitoringService
 * @param themeService
 * @param inactivityService
 * @param msalService
 * @param cmsContentService
 * @param localeService
 * @param chatWidgetService
 * @param authOTokenService
 * @param apiService
 */
export function AuthProviderFactory(
  loginService: LoginService,
  appConfigService: AppConfigService,
  monitoringService: MonitoringService,
  themeService: ThemeService,
  inactivityService: InactivityService,
  msalService: MsalService,
  cmsContentService: FetchCMSContentService,
  localeService: LocaleService,
  chatWidgetService: ChatWidgetService,
  authOTokenService: Auth0TokenService,
  apiService: ApiService,
) {
  // Spec: .github/specs/7740046-auth0-flow-optimizations.md - AC-12.
  // AuthService starts during DI, before this initializer. Never exchange here.
  const isAuth0Callback = authOTokenService.isRedirectCallback;
  if (!isAuth0Callback) {
    authOTokenService.trackStartupSessionCheck('AuthProviderFactory');
  }

  return () => {
    const redirect$: Observable<AuthenticationResult | null> = isAuth0Callback
      ? authOTokenService.waitForStartupCallback().pipe(
          tap((appState) => {
            loginService.state = appState?.target ?? '';
            if (loginService.state) {
              SessionStorageUtility.set(
                SESSION_STORAGE_KEYS.ssoRedirectUri,
                loginService.state,
              );
            }
          }),
          map(() => null),
        )
      : msalService.handleRedirectObservable();

    return redirect$.pipe(
      catchError(() => {
        return of(null);
      }),
      concatMap((res: AuthenticationResult | null): Observable<any> => {
        if (res?.account) {
          msalService.instance.setActiveAccount(res.account);
          authOTokenService.loginSource = LoginSource.B2E;
        }

        return forkJoin([
          loginService.login().pipe(
            tap(() => {
              // set redirectUri in session when coming from B2E/B2C login,
              // this will be read in app.component for navigation after login
              if (res?.state) {
                SessionStorageUtility.set(
                  SESSION_STORAGE_KEYS.ssoRedirectUri,
                  res.state,
                );
              }
              themeService.setThemeVariables();
              loginService.state =
                res?.state ?? (isAuth0Callback ? loginService.state : '');

              if (loginService.state) {
                loginService.setState(loginService.state);
              } else if (!loginService.state && loginService.getState()) {
                loginService.state = loginService.getState() ?? '';
                loginService.clearState();
              }
            }),
            concatMap((result) => {
              return result
                ? appConfigService.loadAppConfig().pipe(
                    tap(() => {
                      inactivityService.loginService = loginService;
                      inactivityService.initializeTimers();
                      monitoringService.setConfig();
                      chatWidgetService.loadChatWidget();
                    }),
                  )
                : of(null);
            }),
            concatMap(() => {
              return localeService.getLocales();
            }),
          ),
          cmsContentService.getCMSContent(endPoints.globalContent, [
            'global',
            'footer_content',
          ]),
          // verify this code block
          loadLoginConfigurations(apiService).then((configs: any[]) => {
            // add app config from api
            authOTokenService.appConfig.update((prevConfig) =>
              (configs ?? []).reduce(
                (appConfig: Record<string, string>, config: IKeyValuePair) => {
                  appConfig[config.key] = config.value;
                  return appConfig;
                },
                prevConfig,
              ),
            );
          }),
        ]);
      }),
    );
  };
}

const loadLoginConfigurations: (apiService: ApiService) => Promise<any> = (
  apiService: ApiService,
): Promise<any> =>
  new Promise((resolve) => {
    apiService
      .callGetApi(endPoints.casemgmt.getAnonymousConfiguration.URI, {})
      .subscribe({
        next: (response: any) => resolve(response?.configurationDetails),
        error: () => resolve([]),
      });
  });
